import { isElementFocusable } from "@digits-shared/helpers/dom"
import colors from "@digits-shared/themes/colors"
import { createGlobalStyle } from "styled-components"

function viewportDimensions() {
  // Sometimes, iOS doesn't apply safe area insets until after scroll events.
  // Trigger layout shift to apply safe-area-inset
  setTimeout(() => {
    window.scrollTo(0, 1)
    window.scrollTo(0, 0)
  }, 100)

  const updateViewportHeight = () => {
    const doc = document.documentElement
    const viewportHeight = window.innerHeight
    doc.style.setProperty("--viewport-height", `${viewportHeight}px`)
  }

  const onOrientationChange = () => {
    setTimeout(() => {
      updateViewportHeight()
    }, 100)
  }

  // Listen for focus events to detect keyboard
  const onFocusChange = (event: FocusEvent) => {
    if (isElementFocusable(event.target)) {
      // Small delay to allow keyboard to appear
      setTimeout(updateViewportHeight, 100)
    }
  }

  // Event listeners
  window.addEventListener("resize", updateViewportHeight)
  window.visualViewport?.addEventListener("resize", updateViewportHeight)
  window.addEventListener("orientationchange", onOrientationChange)
  document.addEventListener("focusin", onFocusChange)
  document.addEventListener("focusout", onFocusChange)

  // Initial setup
  updateViewportHeight()

  return undefined
}

export const GlobalStyles = createGlobalStyle`
  :root {
    color-scheme: light;
    --viewport-height: -webkit-fill-available;
    --viewport-width: -webkit-fill-available;
    --outline-color: ${colors.accentBlue};
    --outline: 2px solid var(--outline-color);

    *:focus-visible {
      outline-color: var(--outline-color);
    }
  }
  ${viewportDimensions};

  :root.desktop {
    &,
    body,
    main {
        margin: 0;
        padding: 0;
        width: 100vw;
        height: 100vh;
        min-height: 100vh;
        min-width: 100vw;
        overscroll-behavior-x: none;
        overscroll-behavior-y: none;
        // prevent unexpected overflow from injected elements
        overflow-y: clip;
    }
  }

  :root.mobile {
    &,
    body,
    main {
        width: 100vw;
        min-width: 100vw;
        margin: 0;
        padding: 0;
        position: relative;
        overflow: hidden;
    }

    body {
        // ios 26.1 fixes safe-area-inset-bottom to account for the toolbox,
        // until then we need to approximate it (140px) to prevent the toolbox from overlapping on top of the content
        // even it leaves gaps in some views
        // update 2: wait until 26.1 to make more drastic changes
        //--safe-area-padding-bottom: clamp(
        //  140px,
        //  env(safe-area-inset-bottom),
        //  env(safe-area-inset-bottom)
        //);
        --safe-area-padding-bottom: env(safe-area-inset-bottom);

        height: var(--viewport-height);
    }

    main {
        height: var(--viewport-height);
        width: var(--viewport-width);
        overflow-y: auto;
    }
  }

  :root.mobile-only  {
    main {
        padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
        env(safe-area-inset-left);
    }

    &.standalone {
        main {
            padding-bottom: 0;
        }
    }
  }

  :root.tablet {
    @media only screen and (orientation: landscape) {
        main {
            padding: env(safe-area-inset-top) 25% env(safe-area-inset-bottom) 25%;
        }
    }

    @media only screen and (orientation: portrait) {
        main {
            padding: env(safe-area-inset-top) 15% env(safe-area-inset-bottom) 15%;
        }
    }
  }

  * {
    box-sizing: border-box;
  }

  body, b, button, input, select {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    font-weight: 400;
    color: inherit;
  }

  button {
    // Chrome's default button font size, standardizing for other browsers (safari, fx)
    // https://lists.w3.org/Archives/Public/www-style/2018Apr/0010.html
    font-size: 13.3333px;
  }

  input {
    border-radius: 0;
  }

  input:focus {
    outline: 0;
  }

  @-webkit-keyframes autofill {
    0%,100% {
      color: inherit;
      background: inherit;
    }
  }

  input:-webkit-autofill {
    -webkit-animation-delay: 1s; /* Safari support - any positive time runs instantly */
    -webkit-animation-name: autofill;
    -webkit-animation-fill-mode: both;
  }

  p {
    margin-bottom: 1rem;
    margin-top: 0;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  b {
    font-weight: 700;
  }

  ul, ol {
    margin: 0;
    padding: 0;
  }

  h2,
  h3,
  h4 {
    margin: 0;
    font-weight: normal;
    font-size: unset;
  }
`
